import React, { useEffect, useRef, useState } from 'react';
import { Stage, Layer, Image as KImage, Text as KText, Rect, Line, Group } from 'react-konva';
import useImage from 'use-image';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Upload, Download, Trash2, RotateCcw } from 'lucide-react';

/* ---------- Canvas dimensions (portrait poster) ---------- */
const W = 1080;
const H = 1350;

/* ---------- Hook: compute Konva stage width directly from viewport (avoids DOM measurement race) ---------- */
function useStageScale(maxWidth = 460) {
  const containerRef = useRef(null);
  const compute = () => {
    if (typeof window === 'undefined') return maxWidth;
    const vw = window.innerWidth;
    // Mobile (< 768): cap at 55% of viewport width and never exceed 320px
    if (vw < 768) return Math.floor(Math.min(vw * 0.55, 320));
    return maxWidth;
  };
  const [width, setWidth] = useState(compute());

  useEffect(() => {
    const update = () => setWidth(compute());
    window.addEventListener('resize', update);
    window.addEventListener('orientationchange', update);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('orientationchange', update);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maxWidth]);

  const scale = width / W;
  return { containerRef, width, height: width * (H / W), scale, ready: width > 0 };
}

/* ---------- Background variants (Diriyah heritage scenes) ---------- */
const BG_VARIANTS = {
  diriyah: '/assets/bg-diriyah.jpg',
  desert: 'https://images.unsplash.com/photo-1645891697649-6548872ce502?w=1600&q=85',
  unification: 'https://images.unsplash.com/photo-1755412734479-c0687daab775?w=1600&q=85',
};

/* ---------- SVG-rendered text: handles Arabic shaping correctly in canvas ---------- */
function makeTextSvgUrl({
  text,
  width,
  fontFamily = 'Cairo, sans-serif',
  fontSize = 40,
  fontWeight = 600,
  fill = '#e6c684',
  letterSpacing = 0,
  glow = false,
  align = 'center',
  rtl = true,
  height,
}) {
  const h = height || Math.ceil(fontSize * 1.7);
  // Build a per-character spacing only when explicitly set (so Arabic words stay joined)
  const ls = letterSpacing ? `letter-spacing:${letterSpacing}px;` : '';
  const filter = glow
    ? `<filter id="g" x="-20%" y="-20%" width="140%" height="140%">
         <feGaussianBlur stdDeviation="3" result="b" />
         <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
       </filter>`
    : '';
  const filterAttr = glow ? 'filter="url(#g)"' : '';
  const anchor = align === 'center' ? 'middle' : align === 'end' ? 'end' : 'start';
  const x = align === 'center' ? width / 2 : align === 'end' ? width - 6 : 6;
  // Escape XML entities
  const safe = (text || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const svg = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${h}" viewBox="0 0 ${width} ${h}">
  <defs>${filter}</defs>
  <text x="${x}" y="${h * 0.7}" text-anchor="${anchor}" direction="${rtl ? 'rtl' : 'ltr'}"
        style="font-family:${fontFamily};font-size:${fontSize}px;font-weight:${fontWeight};${ls}"
        fill="${fill}" ${filterAttr}>${safe}</text>
</svg>`;
  return 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg);
}

function SvgText({ text, y, width = W, x = 0, ...opts }) {
  const url = makeTextSvgUrl({ ...opts, text, width });
  const [img] = useImage(url, 'anonymous');
  if (!img) return null;
  const h = img.naturalHeight || img.height;
  return <KImage image={img} x={x} y={y} width={width} height={h} listening={false} />;
}
function BackgroundLayer({ src }) {
  const [img] = useImage(src, 'anonymous');
  if (!img) return null;
  // cover behavior
  const ir = img.width / img.height;
  const cr = W / H;
  let dw, dh, dx, dy;
  if (ir > cr) {
    dh = H;
    dw = H * ir;
    dx = (W - dw) / 2;
    dy = 0;
  } else {
    dw = W;
    dh = W / ir;
    dx = 0;
    dy = (H - dh) / 2;
  }
  return (
    <KImage
      image={img}
      x={dx}
      y={dy}
      width={dw}
      height={dh}
      listening={false}
    />
  );
}

function CornerOrnaments() {
  // Decorative gold L-shaped corner brackets and small inset frame
  const inset = 40;
  const arm = 90;
  const color = '#d4a574';
  const thin = '#9c7340';
  const corners = [
    { x: inset, y: inset, sx: 1, sy: 1 },
    { x: W - inset, y: inset, sx: -1, sy: 1 },
    { x: inset, y: H - inset, sx: 1, sy: -1 },
    { x: W - inset, y: H - inset, sx: -1, sy: -1 },
  ];
  return (
    <>
      {/* Inner thin frame */}
      <Rect
        x={70}
        y={70}
        width={W - 140}
        height={H - 140}
        stroke={thin}
        strokeWidth={1}
        opacity={0.55}
        listening={false}
      />
      {corners.map((c, i) => (
        <Group key={i} x={c.x} y={c.y} scaleX={c.sx} scaleY={c.sy} listening={false}>
          <Line points={[0, 0, arm, 0]} stroke={color} strokeWidth={2.5} lineCap="round" />
          <Line points={[0, 0, 0, arm]} stroke={color} strokeWidth={2.5} lineCap="round" />
          {/* small diamond at corner */}
          <Line
            points={[0, -8, 8, 0, 0, 8, -8, 0]}
            closed
            fill={color}
            opacity={0.9}
          />
        </Group>
      ))}
    </>
  );
}

function GoldDivider({ y, width = 220 }) {
  const cx = W / 2;
  const half = width / 2;
  return (
    <Group listening={false}>
      <Line points={[cx - half, y, cx - 14, y]} stroke="#d4a574" strokeWidth={1.2} opacity={0.85} />
      <Line points={[cx + 14, y, cx + half, y]} stroke="#d4a574" strokeWidth={1.2} opacity={0.85} />
      <Line
        points={[cx, y - 8, cx + 8, y, cx, y + 8, cx - 8, y]}
        closed
        stroke="#d4a574"
        strokeWidth={1.2}
        fill="#d4a574"
        opacity={0.9}
      />
    </Group>
  );
}

/* ---------- "300" with golden gradient + "عام" beneath ---------- */
function Hero300() {
  return (
    <Group listening={false}>
      <KText
        text="300"
        x={0}
        y={235}
        width={W}
        align="center"
        fontFamily="Playfair Display, Cairo, serif"
        fontStyle="bold"
        fontSize={300}
        fillLinearGradientStartPoint={{ x: 0, y: 0 }}
        fillLinearGradientEndPoint={{ x: 0, y: 300 }}
        fillLinearGradientColorStops={[
          0, '#f3d99a',
          0.5, '#d4a574',
          1, '#8c6027',
        ]}
        shadowColor="#d4a574"
        shadowBlur={28}
        shadowOpacity={0.45}
      />
      <SvgText
        text="عام"
        y={560}
        fontFamily="Cairo, sans-serif"
        fontSize={56}
        fontWeight={700}
        fill="#e6c684"
        glow
      />
    </Group>
  );
}

/* ---------- Logo block (placed in dedicated lower area, above company name) ---------- */
function LogoBlock({ src }) {
  const [img] = useImage(src, 'anonymous');
  if (!img) return null;

  const slotW = 380;
  const slotH = 170;
  const sx = slotW / img.width;
  const sy = slotH / img.height;
  const s = Math.min(sx, sy);
  const drawW = img.width * s;
  const drawH = img.height * s;
  const cx = W / 2;
  const cy = 985; // center of the dedicated logo area (Y 900-1070)

  return (
    <KImage
      image={img}
      x={cx - drawW / 2}
      y={cy - drawH / 2}
      width={drawW}
      height={drawH}
      listening={false}
      shadowColor="#d4a574"
      shadowBlur={18}
      shadowOpacity={0.35}
    />
  );
}

/* ---------- Soft floating dust particles in the canvas ---------- */
function Dust() {
  const particles = [];
  for (let i = 0; i < 80; i++) {
    const x = (i * 137) % W;
    const y = ((i * 89) % (H - 200)) + 100;
    const r = 0.8 + ((i * 13) % 18) / 10;
    const o = 0.05 + ((i * 7) % 20) / 100;
    particles.push(
      <Rect key={i} x={x} y={y} width={r} height={r} fill="#d4a574" opacity={o} cornerRadius={r / 2} listening={false} />,
    );
  }
  return <>{particles}</>;
}

/* ============== Customizer Modal ============== */
export function CustomizerModal({ open, onClose, t, lang }) {
  const c = t.customize;
  const stageRef = useRef(null);
  const fileInputRef = useRef(null);

  const [logoSrc, setLogoSrc] = useState(null);
  const [companyName, setCompanyName] = useState('');
  const [bg, setBg] = useState('diriyah');
  const [busy, setBusy] = useState(false);
  const stage = useStageScale(460);

  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [open]);

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    // Always clear the input value so the same file can be re-uploaded later
    e.target.value = '';
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setLogoSrc(reader.result);
    reader.readAsDataURL(file);
  };

  const reset = () => {
    setLogoSrc(null);
    setCompanyName('');
    setBg('diriyah');
  };

  const download = async () => {
    if (!stageRef.current) return;
    setBusy(true);
    try {
      await new Promise((r) => setTimeout(r, 80));
      const uri = stageRef.current.toDataURL({ pixelRatio: 2, mimeType: 'image/png' });
      const link = document.createElement('a');
      const stamp = (companyName || 'edition').replace(/[^\p{L}\p{N}_-]/gu, '_').slice(0, 24);
      link.download = `founding-day-${stamp}-${Date.now()}.png`;
      link.href = uri;
      link.click();
    } finally {
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-stretch justify-center bg-black/85 backdrop-blur-md md:items-center md:p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          data-testid="customizer-modal"
        >
          <motion.div
            className="relative flex h-[100dvh] w-full max-w-[100vw] flex-col overflow-hidden border border-primary/30 bg-gradient-to-b from-[#1a1510]/95 to-[#0a0806]/95 shadow-[0_0_60px_-10px_rgba(212,165,116,0.4)] md:my-6 md:h-auto md:max-h-[95vh] md:max-w-6xl md:rounded-2xl"
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 30, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          >
            {/* Header */}
            <div className="flex shrink-0 items-center justify-between border-b border-primary/15 px-4 py-3 md:px-8 md:py-4">
              <div className="min-w-0">
                <h3 className="text-gradient-gold font-arabic text-base font-bold leading-[1.6] pb-1 md:text-2xl">
                  {c.modalTitle}
                </h3>
                <p className="mt-0.5 hidden text-xs text-foreground/60 md:block md:text-sm">{c.modalSubtitle}</p>
              </div>
              <button
                type="button"
                onClick={onClose}
                data-testid="modal-close-btn"
                className="ms-3 shrink-0 rounded-full border border-primary/30 p-2 text-primary transition-colors hover:bg-primary/10"
                aria-label={c.closeBtn}
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Body */}
            <div className="grid flex-1 gap-4 overflow-x-hidden overflow-y-auto p-3 md:grid-cols-[1fr_320px] md:gap-6 md:p-8">
              {/* Canvas preview */}
              <div className="flex flex-col">
                <div className="mb-2 hidden items-center justify-between md:flex">
                  <span className="font-arabic text-sm text-primary/80">{c.previewLabel}</span>
                </div>
                <div
                  ref={stage.containerRef}
                  className="relative mx-auto w-full overflow-hidden rounded-xl border border-primary/30 bg-black shadow-[0_30px_60px_-20px_rgba(0,0,0,0.8)]"
                  data-testid="artwork-canvas-wrap"
                  style={{
                    aspectRatio: `${W} / ${H}`,
                    maxWidth: `${stage.width}px`,
                  }}
                >
                  {stage.ready && (
                  <Stage
                    ref={stageRef}
                    width={stage.width}
                    height={stage.height}
                    scaleX={stage.scale}
                    scaleY={stage.scale}
                  >
                    {/* Background */}
                    <Layer listening={false}>
                      <BackgroundLayer src={BG_VARIANTS[bg]} />
                      {/* Dark overlay gradient */}
                      <Rect
                        x={0}
                        y={0}
                        width={W}
                        height={H}
                        fillLinearGradientStartPoint={{ x: 0, y: 0 }}
                        fillLinearGradientEndPoint={{ x: 0, y: H }}
                        fillLinearGradientColorStops={[
                          0, 'rgba(10,8,6,0.78)',
                          0.45, 'rgba(10,8,6,0.55)',
                          1, 'rgba(10,8,6,0.95)',
                        ]}
                      />
                      <Rect
                        x={0}
                        y={0}
                        width={W}
                        height={H}
                        fillRadialGradientStartPoint={{ x: W / 2, y: H / 2 }}
                        fillRadialGradientStartRadius={0}
                        fillRadialGradientEndPoint={{ x: W / 2, y: H / 2 }}
                        fillRadialGradientEndRadius={W * 0.7}
                        fillRadialGradientColorStops={[
                          0, 'rgba(212,165,116,0.18)',
                          1, 'rgba(0,0,0,0)',
                        ]}
                      />
                    </Layer>

                    {/* Decorative dust */}
                    <Layer listening={false}>
                      <Dust />
                    </Layer>

                    {/* Foreground composition */}
                    <Layer listening={false}>
                      <CornerOrnaments />

                      {/* 1. Top headline */}
                      <SvgText
                        text={c.headline}
                        y={110}
                        fontFamily="Cairo, sans-serif"
                        fontSize={56}
                        fontWeight={700}
                        fill="#e6c684"
                        glow
                      />
                      <GoldDivider y={210} width={260} />

                      {/* 2 & 3. "300" + "عام" */}
                      <Hero300 />

                      {/* 4. Years */}
                      <KText
                        text={c.yearsText}
                        x={0}
                        y={660}
                        width={W}
                        align="center"
                        fontFamily="Playfair Display, Cairo, serif"
                        fontStyle="bold"
                        fontSize={38}
                        fill="#d4a574"
                        letterSpacing={6}
                      />

                      {/* 5. Tagline */}
                      <SvgText
                        text={c.tagline}
                        y={715}
                        fontFamily="Cairo, sans-serif"
                        fontSize={26}
                        fontWeight={500}
                        fill="rgba(230,198,132,0.78)"
                        rtl={lang === 'ar'}
                      />

                      {/* spacer divider */}
                      <GoldDivider y={830} width={180} />

                      {/* 7. Logo (or placeholder) */}
                      {logoSrc ? (
                        <LogoBlock src={logoSrc} />
                      ) : (
                        <>
                          <Rect
                            x={W / 2 - 200}
                            y={895}
                            width={400}
                            height={185}
                            stroke="#d4a574"
                            strokeWidth={1.2}
                            dash={[6, 6]}
                            cornerRadius={8}
                            opacity={0.45}
                          />
                          <SvgText
                            text={lang === 'ar' ? 'مكان شعار شركتك' : 'Your Company Logo'}
                            y={970}
                            fontFamily="Cairo, sans-serif"
                            fontSize={28}
                            fontWeight={500}
                            fill="rgba(230,198,132,0.5)"
                            rtl={lang === 'ar'}
                          />
                        </>
                      )}

                      {/* 8. Company name */}
                      <SvgText
                        text={companyName || (lang === 'ar' ? 'اسم الموظف هنا' : 'Employee name here')}
                        y={1130}
                        fontFamily="Cairo, sans-serif"
                        fontSize={48}
                        fontWeight={700}
                        fill={companyName ? '#f3d99a' : 'rgba(243,217,154,0.45)'}
                        rtl={lang === 'ar'}
                        glow={!!companyName}
                      />
                    </Layer>
                  </Stage>
                  )}
                </div>
              </div>

              {/* Controls */}
              <div className="space-y-3 md:space-y-5">
                {/* Upload */}
                <div className="rounded-xl border border-primary/20 bg-background/40 p-3 md:p-4">
                  <div className="mb-2 font-arabic text-sm font-semibold text-primary">
                    {c.uploadLabel}
                  </div>
                  <p className="mb-3 text-xs text-foreground/55">{c.uploadHint}</p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/svg+xml,image/webp"
                    onChange={handleFile}
                    className="hidden"
                    data-testid="logo-file-input"
                  />
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      data-testid="logo-upload-btn"
                      className="inline-flex flex-1 items-center justify-center gap-2 rounded-full border border-primary/60 bg-primary/10 px-4 py-2 text-sm font-semibold text-primary transition-all hover:bg-primary/20"
                    >
                      <Upload className="h-4 w-4" />
                      {c.uploadButton}
                    </button>
                    {logoSrc && (
                      <button
                        type="button"
                        onClick={() => setLogoSrc(null)}
                        data-testid="logo-remove-btn"
                        className="inline-flex items-center justify-center rounded-full border border-destructive/40 bg-destructive/10 p-2 text-destructive/80 transition-colors hover:bg-destructive/20"
                        aria-label={c.removeLogo}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Company name */}
                <div className="rounded-xl border border-primary/20 bg-background/40 p-3 md:p-4">
                  <label className="mb-2 block font-arabic text-sm font-semibold text-primary">
                    {c.companyLabel}
                  </label>
                  <input
                    type="text"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    placeholder={c.companyPlaceholder}
                    data-testid="company-name-input"
                    maxLength={40}
                    className="w-full rounded-lg border border-primary/25 bg-background/60 px-3 py-2 text-sm text-foreground outline-none transition-colors placeholder:text-foreground/40 focus:border-primary/60"
                  />
                </div>

                {/* Background variants */}
                <div className="rounded-xl border border-primary/20 bg-background/40 p-3 md:p-4">
                  <div className="mb-3 font-arabic text-sm font-semibold text-primary">
                    {c.backgroundLabel}
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {c.bgOptions.map((opt) => (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() => setBg(opt.id)}
                        data-testid={`bg-option-${opt.id}`}
                        className={`group relative aspect-[3/4] overflow-hidden rounded-md border transition-all ${
                          bg === opt.id
                            ? 'border-primary shadow-[0_0_18px_-4px_rgba(212,165,116,0.7)]'
                            : 'border-primary/15 opacity-70 hover:opacity-100'
                        }`}
                      >
                        <img
                          src={BG_VARIANTS[opt.id]}
                          alt={opt.label}
                          className="h-full w-full object-cover"
                          loading="lazy"
                        />
                        <span className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
                        <span className="absolute bottom-1 left-0 right-0 text-center font-arabic text-[10px] text-primary">
                          {opt.label}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Actions (desktop only inline) */}
                <div className="hidden flex-col gap-2 md:flex">
                  <button
                    type="button"
                    onClick={download}
                    disabled={busy}
                    data-testid="download-btn"
                    className="group relative inline-flex items-center justify-center gap-2 overflow-hidden rounded-full bg-gradient-to-r from-[#a47436] via-[#e6c684] to-[#a47436] px-6 py-3 text-sm font-bold text-[#1a1107] shadow-[0_8px_24px_-8px_rgba(212,165,116,0.7)] transition-transform hover:scale-[1.02] disabled:opacity-60"
                  >
                    <Download className="h-4 w-4" />
                    {busy ? '...' : c.downloadBtn}
                  </button>
                  <button
                    type="button"
                    onClick={reset}
                    data-testid="reset-btn"
                    className="inline-flex items-center justify-center gap-2 rounded-full border border-primary/30 px-6 py-2 text-xs text-primary/80 transition-colors hover:bg-primary/10"
                  >
                    <RotateCcw className="h-3.5 w-3.5" />
                    {c.resetBtn}
                  </button>
                </div>
              </div>
            </div>

            {/* Sticky mobile action bar */}
            <div className="flex shrink-0 items-center gap-2 border-t border-primary/15 bg-background/80 p-3 backdrop-blur md:hidden">
              <button
                type="button"
                onClick={reset}
                data-testid="reset-btn-mobile"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-primary/30 px-4 py-2.5 text-xs text-primary/80"
              >
                <RotateCcw className="h-3.5 w-3.5" />
                {c.resetBtn}
              </button>
              <button
                type="button"
                onClick={download}
                disabled={busy}
                data-testid="download-btn-mobile"
                className="inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#a47436] via-[#e6c684] to-[#a47436] px-4 py-2.5 text-sm font-bold text-[#1a1107] shadow-[0_8px_24px_-8px_rgba(212,165,116,0.7)] disabled:opacity-60"
              >
                <Download className="h-4 w-4" />
                {busy ? '...' : c.downloadBtn}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/* ============== CTA Section ============== */
export default function CustomizeCTA({ t, lang }) {
  const [open, setOpen] = useState(false);
  const c = t.customize;

  return (
    <>
      <section
        id="customize"
        data-testid="customize-cta-section"
        className="relative overflow-hidden py-20 md:py-28"
      >
        <div
          aria-hidden="true"
          className="pointer-events-none absolute left-1/2 top-1/2 -z-10 h-[600px] w-[600px] -translate-x-1/2 -translate-y-1/2 rounded-full"
          style={{
            background:
              'radial-gradient(closest-side, rgba(212,165,116,0.25), rgba(212,165,116,0.05) 55%, transparent 78%)',
          }}
        />

        <motion.div
          className="container mx-auto max-w-3xl px-6 text-center"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          viewport={{ once: true, amount: 0.4 }}
        >
          <span className="inline-block text-xs font-semibold uppercase tracking-[0.4em] text-primary/70">
            {c.eyebrow}
          </span>
          <h2
            data-testid="customize-cta-title"
            className="text-gradient-gold font-arabic mt-4 text-3xl font-bold leading-[1.5] pb-2 md:text-5xl"
          >
            {c.title}
          </h2>
          <div className="mt-5 gold-divider mx-auto w-20" />
          <p className="mx-auto mt-6 max-w-xl text-base leading-loose text-foreground/70 md:text-lg">
            {c.subtitle}
          </p>

          <motion.button
            type="button"
            onClick={() => setOpen(true)}
            data-testid="customize-cta-btn"
            className="group relative mt-10 inline-flex items-center gap-3 overflow-hidden rounded-full px-10 py-4 text-base font-bold tracking-wide md:text-lg"
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.98 }}
          >
            <span className="absolute inset-0 rounded-full bg-gradient-to-r from-[#8a5b27] via-[#e8c987] to-[#8a5b27]" />
            <span className="absolute inset-0 overflow-hidden rounded-full">
              <span className="absolute -inset-y-2 -left-1/2 w-1/2 rotate-12 bg-white/30 blur-md transition-transform duration-1000 group-hover:translate-x-[300%]" />
            </span>
            <span className="absolute -inset-1 rounded-full bg-primary/30 blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            <span className="relative z-10 font-arabic text-[#1a1107]">{c.cta}</span>
            <svg
              viewBox="0 0 24 24"
              className="relative z-10 h-5 w-5 text-[#1a1107] transition-transform duration-500 group-hover:translate-x-1 rtl:rotate-180 rtl:group-hover:-translate-x-1"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M5 12h14M13 5l7 7-7 7" />
            </svg>
          </motion.button>
        </motion.div>
      </section>

      <CustomizerModal open={open} onClose={() => setOpen(false)} t={t} lang={lang} />
    </>
  );
}
